// src/components/BatteryCarousel.tsx
import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Battery, TrendingUp, TrendingDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

interface BatteryData {
  id: string;
  name: string;
  currentCharge: number;
  health: number;
  cycles?: number;
  serialNumber?: string;
  purchaseDate?: string;
  lastCharged?: string;
  status: 'healthy' | 'warning' | 'critical' | 'dead';
}

interface BatteryCarouselProps {
  batteries: BatteryData[];
  deviceName: string;
  deviceId?: string;
}

interface HistoryDataPoint {
  timestamp: string;
  charge: number;
  health: number;
}

export const BatteryCarousel: React.FC<BatteryCarouselProps> = ({
  batteries,
  deviceName,
  deviceId
}) => {
  const { currentUser } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [historyData, setHistoryData] = useState<Record<string, HistoryDataPoint[]>>({});

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return '#10B981';
      case 'warning': return '#F59E0B';
      case 'critical': return '#EF4444';
      case 'dead': return '#6B7280';
      default: return '#6B7280';
    }
  };

  const getStatusEmoji = (status: string) => {
    switch (status) {
      case 'healthy': return '✅';
      case 'warning': return '⚠️';
      case 'critical': return '🔴';
      case 'dead': return '💀';
      default: return '❓';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'healthy': return 'Gesund';
      case 'warning': return 'Warnung';
      case 'critical': return 'Kritisch';
      case 'dead': return 'Tot';
      default: return 'Unbekannt';
    }
  };

  // Load history data for each battery
  useEffect(() => {
    const loadHistory = async () => {
      if (!currentUser || !deviceId) return;

      const newHistoryData: Record<string, HistoryDataPoint[]> = {};

      for (const battery of batteries) {
        try {
          const historyRef = collection(db, 'users', currentUser.uid, 'devices', deviceId, 'batteryHistory');
          const historyQuery = query(
            historyRef,
            where('batteryId', '==', battery.id),
            orderBy('timestamp', 'desc'),
            limit(7)
          );

          const snapshot = await getDocs(historyQuery);
          const data: HistoryDataPoint[] = [];

          snapshot.forEach((doc) => {
            const historyData = doc.data();
            data.push({
              timestamp: historyData.timestamp,
              charge: historyData.currentCharge || 0,
              health: historyData.health || 100
            });
          });

          // Reverse to show oldest first
          newHistoryData[battery.id] = data.reverse();
        } catch (error) {
          console.error(`Error loading history for battery ${battery.id}:`, error);
          newHistoryData[battery.id] = [];
        }
      }

      setHistoryData(newHistoryData);
    };

    loadHistory();
  }, [batteries, currentUser, deviceId]);

  // Simple chart component
  const MiniChart: React.FC<{ data: HistoryDataPoint[]; currentCharge: number }> = ({ data, currentCharge }) => {
    if (data.length === 0) {
      return (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          background: '#F9FAFB',
          borderRadius: '10px',
          color: '#9CA3AF',
          fontSize: '0.9rem'
        }}>
          <TrendingUp size={24} style={{ margin: '0 auto 0.5rem' }} />
          <div>Noch keine Verlaufsdaten vorhanden</div>
          <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>Daten werden beim nächsten Update aufgezeichnet</div>
        </div>
      );
    }

    const maxCharge = Math.max(...data.map(d => d.charge), currentCharge);
    const minCharge = Math.min(...data.map(d => d.charge), currentCharge);
    const range = maxCharge - minCharge || 1;

    const points = data.map((d, i) => ({
      x: (i / (data.length - 1 || 1)) * 100,
      y: 100 - ((d.charge - minCharge) / range) * 100
    }));

    const pathD = points.map((p, i) =>
      `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
    ).join(' ');

    // Calculate trend
    const firstCharge = data[0]?.charge || currentCharge;
    const lastCharge = data[data.length - 1]?.charge || currentCharge;
    const trend = lastCharge - firstCharge;

    return (
      <div style={{
        padding: '1rem',
        background: '#F9FAFB',
        borderRadius: '10px',
        marginTop: '1rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem'
        }}>
          <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6B7280' }}>
            Verlauf (letzte {data.length} {data.length === 1 ? 'Eintrag' : 'Einträge'})
          </span>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '0.8rem',
            color: trend >= 0 ? '#10B981' : '#EF4444',
            fontWeight: '600'
          }}>
            {trend >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            {trend >= 0 ? '+' : ''}{trend.toFixed(0)}%
          </div>
        </div>

        <svg
          viewBox="0 0 100 50"
          style={{
            width: '100%',
            height: '120px',
            overflow: 'visible'
          }}
        >
          {/* Grid lines */}
          <line x1="0" y1="0" x2="100" y2="0" stroke="#E5E7EB" strokeWidth="0.5" />
          <line x1="0" y1="25" x2="100" y2="25" stroke="#E5E7EB" strokeWidth="0.5" strokeDasharray="2,2" />
          <line x1="0" y1="50" x2="100" y2="50" stroke="#E5E7EB" strokeWidth="0.5" />

          {/* Area under curve */}
          <path
            d={`${pathD} L 100 50 L 0 50 Z`}
            fill="url(#gradient)"
            opacity="0.3"
          />

          {/* Line */}
          <path
            d={pathD}
            fill="none"
            stroke="#667eea"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points */}
          {points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r="2.5"
              fill="#667eea"
              stroke="white"
              strokeWidth="1.5"
            />
          ))}

          {/* Gradient definition */}
          <defs>
            <linearGradient id="gradient" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#667eea" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#764ba2" stopOpacity="0.1" />
            </linearGradient>
          </defs>
        </svg>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.7rem',
          color: '#9CA3AF',
          marginTop: '0.5rem'
        }}>
          <span>{data[0] ? new Date(data[0].timestamp).toLocaleDateString('de-DE', { month: 'short', day: 'numeric' }) : ''}</span>
          <span>Heute: {currentCharge}%</span>
        </div>
      </div>
    );
  };

  const nextBattery = () => {
    setCurrentIndex((prev) => (prev + 1) % batteries.length);
  };

  const prevBattery = () => {
    setCurrentIndex((prev) => (prev - 1 + batteries.length) % batteries.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      // Swipe left - next battery
      nextBattery();
    } else if (distance < -minSwipeDistance) {
      // Swipe right - previous battery
      prevBattery();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  if (batteries.length === 0) {
    return null;
  }

  const currentBattery = batteries[currentIndex];

  return (
    <div style={{
      marginTop: '1.5rem',
      position: 'relative'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1.1rem',
          fontWeight: '600',
          color: '#2E3A4B'
        }}>
          🔋 Zusatzakkus
        </h3>
        <div style={{
          padding: '4px 12px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: 'bold'
        }}>
          {currentIndex + 1} / {batteries.length}
        </div>
      </div>

      {/* Carousel Container */}
      <div
        ref={carouselRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          position: 'relative',
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: `3px solid ${getStatusColor(currentBattery.status)}`,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          cursor: 'grab',
          transition: 'border-color 0.3s ease'
        }}
      >
        {/* Battery Card Content */}
        <div style={{
          textAlign: 'center'
        }}>
          {/* Battery Name */}
          <h4 style={{
            margin: '0 0 1rem 0',
            fontSize: '1.3rem',
            fontWeight: '700',
            color: '#2E3A4B'
          }}>
            {currentBattery.name}
          </h4>

          {/* Status Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 16px',
            background: `${getStatusColor(currentBattery.status)}15`,
            border: `2px solid ${getStatusColor(currentBattery.status)}`,
            borderRadius: '20px',
            marginBottom: '1.5rem'
          }}>
            <span style={{ fontSize: '1.2rem' }}>
              {getStatusEmoji(currentBattery.status)}
            </span>
            <span style={{
              fontSize: '0.9rem',
              color: getStatusColor(currentBattery.status),
              fontWeight: '600'
            }}>
              {getStatusText(currentBattery.status)}
            </span>
          </div>

          {/* Main Stats Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            {/* Charge */}
            <div style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
              borderRadius: '12px',
              border: '2px solid #E5E7EB'
            }}>
              <div style={{
                fontSize: '0.8rem',
                color: '#6B7280',
                marginBottom: '0.5rem',
                fontWeight: '600'
              }}>
                Ladung
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}>
                <Battery
                  size={28}
                  color={
                    currentBattery.currentCharge > 50 ? '#10B981' :
                    currentBattery.currentCharge > 20 ? '#F59E0B' : '#EF4444'
                  }
                  fill={
                    currentBattery.currentCharge > 50 ? '#10B98120' :
                    currentBattery.currentCharge > 20 ? '#F59E0B20' : '#EF444420'
                  }
                />
                <span style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#2E3A4B'
                }}>
                  {currentBattery.currentCharge}%
                </span>
              </div>
            </div>

            {/* Health */}
            <div style={{
              padding: '1rem',
              background: 'linear-gradient(135deg, #10B98115 0%, #059669' + '15 100%)',
              borderRadius: '12px',
              border: '2px solid #E5E7EB'
            }}>
              <div style={{
                fontSize: '0.8rem',
                color: '#6B7280',
                marginBottom: '0.5rem',
                fontWeight: '600'
              }}>
                Gesundheit
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '1.8rem' }}>💚</span>
                <span style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#2E3A4B'
                }}>
                  {currentBattery.health}%
                </span>
              </div>
            </div>
          </div>

          {/* History Chart */}
          <MiniChart
            data={historyData[currentBattery.id] || []}
            currentCharge={currentBattery.currentCharge}
          />

          {/* Additional Info */}
          <div style={{
            display: 'grid',
            gap: '0.75rem',
            textAlign: 'left',
            padding: '1rem',
            background: '#F9FAFB',
            borderRadius: '10px',
            marginTop: '1rem'
          }}>
            {currentBattery.cycles !== undefined && currentBattery.cycles > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.9rem',
                color: '#374151'
              }}>
                <span style={{ fontSize: '1.2rem' }}>🔄</span>
                <span><strong>{currentBattery.cycles}</strong> Ladezyklen</span>
              </div>
            )}
            {currentBattery.serialNumber && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.9rem',
                color: '#374151'
              }}>
                <span style={{ fontSize: '1.2rem' }}>🔢</span>
                <span>S/N: <strong>{currentBattery.serialNumber}</strong></span>
              </div>
            )}
            {currentBattery.purchaseDate && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.9rem',
                color: '#374151'
              }}>
                <span style={{ fontSize: '1.2rem' }}>📅</span>
                <span>Gekauft: <strong>{new Date(currentBattery.purchaseDate).toLocaleDateString('de-DE')}</strong></span>
              </div>
            )}
            {currentBattery.lastCharged && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.9rem',
                color: '#374151'
              }}>
                <span style={{ fontSize: '1.2rem' }}>⚡</span>
                <span>Zuletzt geladen: <strong>{new Date(currentBattery.lastCharged).toLocaleDateString('de-DE')}</strong></span>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Arrows */}
        {batteries.length > 1 && (
          <>
            <button
              onClick={prevBattery}
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'white',
                border: '2px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s',
                zIndex: 10
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#FF6B35';
                e.currentTarget.style.borderColor = '#FF6B35';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#E5E7EB';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <ChevronLeft size={24} color="#2E3A4B" />
            </button>

            <button
              onClick={nextBattery}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'white',
                border: '2px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s',
                zIndex: 10
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#FF6B35';
                e.currentTarget.style.borderColor = '#FF6B35';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#E5E7EB';
                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              }}
            >
              <ChevronRight size={24} color="#2E3A4B" />
            </button>
          </>
        )}
      </div>

      {/* Dot Indicators */}
      {batteries.length > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginTop: '1rem'
        }}>
          {batteries.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              style={{
                width: index === currentIndex ? '24px' : '8px',
                height: '8px',
                borderRadius: '4px',
                background: index === currentIndex ? '#FF6B35' : '#D1D5DB',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
              aria-label={`Akku ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Swipe Hint (only shown on first render) */}
      {batteries.length > 1 && (
        <div style={{
          textAlign: 'center',
          marginTop: '0.75rem',
          fontSize: '0.75rem',
          color: '#9CA3AF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px'
        }}>
          <span>👆</span>
          <span>Wischen oder Pfeile verwenden</span>
        </div>
      )}
    </div>
  );
};
